import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ChevronRight, Clock, Sparkles, Trophy } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact } from '../../../utils/haptic'
import { formatPrice } from '../../../utils/formatters'
import useCountdown from '../hooks/useCountdown'
import eventApi from '../services/eventApi'
import { HALLOWEEN_POSTER, formatEventTimer } from '../utils/halloween'
import { PumpkinIcon } from './halloween/shared'

// Эвент длится недели — профиль обновляем неторопливо и только при видимой вкладке
const BANNER_POLL_INTERVAL = 60000

const CHIP_CLASS = 'inline-flex items-center gap-1 rounded-lg border bg-dark-primary/70 px-2 py-1 text-[11px] font-bold tabular-nums backdrop-blur-sm'

/**
 * Баннер эвента «Проклятие Skinoz» на главной.
 * Участнику данные приходят в me.halloween_event (/api/me/): ключ есть — баннер рисуем,
 * ключа нет — эвента нет. Гостю /api/me/ недоступен — берём страницу эвента
 * (GET /api/event/halloween/, { enabled: false } — баннера нет).
 */
const HalloweenBanner = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isGuest = useAppStore((state) => state.isGuest)
  const memberEvent = useAppStore((state) => state.user?.halloween_event)
  const loadProfile = useAppStore((state) => state.loadProfile)
  const { playSound } = useAudioManager({ select: 'select' })
  const [guestEvent, setGuestEvent] = useState(null)

  // Участник: окно эвента открывается и закрывается само — профиль обновляем сами
  useEffect(() => {
    if (isGuest) return undefined
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadProfile().catch(() => {})
    }, BANNER_POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [isGuest, loadProfile])

  // Гость: один запрос страницы эвента при открытии главной
  useEffect(() => {
    if (!isGuest) return undefined
    const controller = new AbortController()
    eventApi.getHalloweenEvent(controller.signal)
      .then((response) => setGuestEvent(response?.enabled ? response : null))
      .catch(() => {})
    return () => controller.abort()
  }, [isGuest])

  const event = isGuest ? guestEvent : memberEvent
  const phase = event?.phase
  const endsRemaining = useCountdown(phase === 'active' ? event?.ends_in : null)
  const drawRemaining = useCountdown(phase === 'soon_draw' ? event?.draw_in : null)

  if (!event) return null

  const today = event.you?.today
  const witch = event.witch
  const won = event.result?.won

  const handleOpen = () => {
    playSound('select')
    hapticImpact('light')
    navigate('/event')
  }

  const description = {
    active: t('halloweenEvent.tagline'),
    soon_draw: t('halloweenEvent.banner.soonDraw'),
    drawing: t('halloweenEvent.banner.drawing'),
    results: won ? t('halloweenEvent.banner.won') : t('halloweenEvent.banner.results')
  }[phase] || t('halloweenEvent.tagline')

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35 }}
      onClick={handleOpen}
      className="flex w-full flex-col overflow-hidden rounded-2xl border border-orange-400/35 bg-[#0d0a12] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_18px_40px_-18px_rgba(255,106,0,0.45)] transition-all hover:border-orange-400/60 active:scale-[0.98]"
    >
      <div className="relative">
        <img
          src={HALLOWEEN_POSTER}
          alt={t('halloweenEvent.title')}
          className="aspect-[4/3] w-full object-cover"
          loading="lazy"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent"
          aria-hidden="true"
        />

        {/* Чипы поверх нижнего градиента: таймер фазы, дневные тыквы, сытость ведьмы */}
        <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-1.5 p-3">
          {phase === 'active' && (
            <span className={`${CHIP_CLASS} border-orange-400/40 text-orange-100`}>
              <Clock size={12} className="shrink-0 text-orange-300" />
              {formatEventTimer(endsRemaining, t)}
            </span>
          )}
          {phase === 'soon_draw' && (
            <span className={`${CHIP_CLASS} border-violet-400/40 text-violet-100`}>
              <Clock size={12} className="shrink-0 text-violet-300" />
              {t('halloweenEvent.banner.drawIn', { time: formatEventTimer(drawRemaining, t) })}
            </span>
          )}
          {phase === 'drawing' && (
            <span className={`${CHIP_CLASS} border-violet-400/40 text-violet-100`}>
              <Sparkles size={12} className="shrink-0 animate-pulse text-violet-300" />
              {t('halloweenEvent.draw.pulling')}
            </span>
          )}
          {phase === 'results' && (
            <span className={`${CHIP_CLASS} border-yellow-400/40 text-yellow-100`}>
              <Trophy size={12} className="shrink-0 text-yellow-300" />
              {won && event.result?.amount
                ? `${formatPrice(event.result.amount)} 💎`
                : t('halloweenEvent.draw.titleResults')}
            </span>
          )}
          {phase === 'active' && today && event.you?.state !== 'no_team' && (
            <span className={`${CHIP_CLASS} border-orange-400/40 text-orange-100`}>
              <PumpkinIcon size={13} />
              {t('halloweenEvent.banner.today', { count: today.pumpkins, cap: today.cap })}
            </span>
          )}
          {witch && phase === 'active' && (
            <span className={`${CHIP_CLASS} border-lime-400/40 text-lime-100`}>
              {witch.fed
                ? t('halloweenEvent.banner.witchFed')
                : t('halloweenEvent.banner.witch', { pct: witch.pct ?? 0 })}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 p-3">
        <PumpkinIcon size={34} glow />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-orange-300">
            {t('halloweenEvent.title')}
          </p>
          <p className="mt-2 text-sm font-semibold text-white">{description}</p>
        </div>
        <ChevronRight size={16} className="shrink-0 text-orange-300 rtl:rotate-180" />
      </div>
    </motion.button>
  )
}

export default HalloweenBanner
